import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDays, ChevronLeft, Clock, Loader2, Pencil, User } from 'lucide-react';
import { DatePicker } from './date-picker';
import { TimeSlotGrid } from './time-slot-grid';
import { ContactForm } from './contact-form';

// Combined date + time + basics picker shown as one form step.
// Progressive disclosure: each selection collapses to a pill and reveals
// the next thing.
//
//   no date           -> show calendar
//   date but no time  -> date pill + time grid
//   date + time       -> date pill + time pill + basics fields
//
// Desktop and mobile share the same progression. On desktop the calendar
// and time grid render side-by-side until a time is picked, then both
// collapse to the pill row and basics fills the width.
export function TimePickStep({
  slotsLoading,
  availableDates,
  slotsByDate,
  selectedDate,
  selectedSlot,
  onDateSelect,
  onSlotSelect,
  formData,
  onFormDataChange,
  formConfig,
  onBasicsBlur,
}) {
  // Mobile: explicit "reopen calendar" state. Tracks whether the user has
  // tapped the back-to-date pill so we can ignore selectedDate temporarily.
  const [mobileShowDate, setMobileShowDate] = useState(false);

  if (slotsLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading available dates&hellip;
      </div>
    );
  }

  // No open dates - usually because the form's booking window has closed for
  // the current intake. Show a friendly note instead of an empty calendar.
  if (!availableDates || availableDates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border py-16 text-center">
        <CalendarDays className="size-6 text-muted-foreground" />
        <p className="text-sm font-medium">Booking is currently closed</p>
        <p className="max-w-sm text-xs text-muted-foreground">
          There are no open dates for this intake right now. Please check back soon
          or reach out and we&apos;ll help you join the next cohort.
        </p>
      </div>
    );
  }

  const dateLabel = selectedDate ? format(parseISO(selectedDate), 'EEE, MMM d') : null;
  const timeLabel = selectedSlot ? format(parseISO(selectedSlot.time), 'h:mm a') : null;
  const mobileShowingTimes = selectedDate && !mobileShowDate;
  // Once a time is selected we collapse the pickers to a pill row and
  // reveal the basics form. This is the same flow on both desktop and
  // mobile so we render it once at the top.
  const showingBasics = !!selectedSlot;

  // Pill bar: shows the current date + time, each tappable to re-open.
  const PillBar = () => (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => {
          // Clear both so the calendar is the next thing shown.
          onSlotSelect(null);
          onDateSelect(null);
          setMobileShowDate(true);
        }}
        className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary-soft/50 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-primary-soft/70"
      >
        <CalendarDays className="size-3.5 text-primary" />
        {dateLabel}
        <Pencil className="size-3 text-muted-foreground" />
      </button>
      <button
        type="button"
        onClick={() => {
          // Keep the date, just drop the time so the time grid re-opens.
          onSlotSelect(null);
        }}
        className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary-soft/50 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-primary-soft/70"
      >
        <Clock className="size-3.5 text-primary" />
        {timeLabel}
        <Pencil className="size-3 text-muted-foreground" />
      </button>
    </div>
  );

  if (showingBasics) {
    return (
      <div className="space-y-5 animate-fade-in">
        <PillBar />
        <div>
          <div className="mb-3 flex items-center gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
              <User className="size-3.5" />
            </div>
            <h3 className="text-sm font-semibold tracking-tight">About you</h3>
          </div>
          <ContactForm formData={formData} onChange={onFormDataChange} formConfig={formConfig} onFieldBlur={onBasicsBlur} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ─── Desktop layout: two columns, always visible ────────────────── */}
      <div className="hidden grid-cols-2 gap-6 md:grid">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
              <CalendarDays className="size-3.5" />
            </div>
            <h3 className="text-sm font-semibold tracking-tight">Pick a date</h3>
          </div>
          <DatePicker
            availableDates={availableDates}
            selectedDate={selectedDate}
            onSelect={onDateSelect}
          />
        </div>

        <div className="border-l border-border pl-6">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
              <Clock className="size-3.5" />
            </div>
            <h3 className="text-sm font-semibold tracking-tight">
              {selectedDate
                ? `Times on ${format(parseISO(selectedDate), 'EEEE, MMM d')}`
                : 'Pick a date to see times'}
            </h3>
          </div>

          {selectedDate ? (
            <div className="animate-fade-in">
              <TimeSlotGrid
                slots={slotsByDate[selectedDate] || []}
                selectedSlot={selectedSlot}
                onSelect={onSlotSelect}
                compact
              />
            </div>
          ) : (
            <div className="flex h-[280px] items-center justify-center rounded-md border border-dashed border-border text-center text-xs text-muted-foreground">
              Click a date to see open slots.
            </div>
          )}
        </div>
      </div>

      {/* ─── Mobile layout: calendar replaces with time grid on date click ── */}
      <div className="md:hidden">
        {mobileShowingTimes ? (
          <div className="space-y-3 animate-fade-in">
            <button
              type="button"
              onClick={() => setMobileShowDate(true)}
              className="flex w-full items-center justify-between rounded-md border border-input bg-primary-soft/40 px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-primary-soft/60"
            >
              <span className="flex items-center gap-2">
                <CalendarDays className="size-4 text-primary" />
                {dateLabel}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <ChevronLeft className="size-3.5" />
                change
              </span>
            </button>

            <div className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
                <Clock className="size-3.5" />
              </div>
              <h3 className="text-sm font-semibold tracking-tight">Pick a time</h3>
            </div>
            <TimeSlotGrid
              slots={slotsByDate[selectedDate] || []}
              selectedSlot={selectedSlot}
              onSelect={onSlotSelect}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
                <CalendarDays className="size-3.5" />
              </div>
              <h3 className="text-sm font-semibold tracking-tight">Pick a date</h3>
            </div>
            <DatePicker
              availableDates={availableDates}
              selectedDate={selectedDate}
              onSelect={(d) => {
                onDateSelect(d);
                setMobileShowDate(false);
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
